import { Coordinates, PrayerTimes, SunnahTimes } from "adhan";
import {
  formatClock,
  formatDateLabel,
  formatTime,
  getDateForTimeZone,
  getStartOfNextDayInTimeZone,
  shortTimeZone,
} from "@/lib/formatting";
import { createCalculationParameters } from "@/lib/prayer-methods";
import type { PrayerEvent, PrayerLocation, PrayerMethodConfig, PrayerSnapshot, PrayerTiming } from "@/types/prayer";
import { prayerLabels } from "@/types/prayer";

const isFard = (event: PrayerEvent) => ["asr", "dhuhr", "fajr", "isha", "maghrib"].includes(event);

const buildTimings = (prayerTimes: PrayerTimes, sunnahTimes: SunnahTimes, timeZone: string): PrayerTiming[] => {
  const entries: Record<PrayerEvent, Date> = {
    asr: prayerTimes.asr,
    dhuhr: prayerTimes.dhuhr,
    fajr: prayerTimes.fajr,
    isha: prayerTimes.isha,
    lastThirdOfTheNight: sunnahTimes.lastThirdOfTheNight,
    maghrib: prayerTimes.maghrib,
    middleOfTheNight: sunnahTimes.middleOfTheNight,
    sunrise: prayerTimes.sunrise,
  };

  return Object.entries(entries)
    .sort(([, left], [, right]) => left.getTime() - right.getTime())
    .map(([event, value]) => {
      const typedEvent = event as PrayerEvent;

      return {
        event: typedEvent,
        isFard: isFard(typedEvent),
        label: prayerLabels[typedEvent],
        time: formatTime(value, timeZone),
        value,
      };
    });
};

export const getActiveEvent = (timings: PrayerTiming[], timestamp: number): PrayerEvent | null => {
  if (timings.length === 0) {
    return null;
  }

  for (let index = timings.length - 1; index >= 0; index -= 1) {
    const timing = timings[index];

    if (timing && timing.value.getTime() <= timestamp) {
      return timing.event;
    }
  }

  const nightEvents = timings.slice(-3);
  const oneDay = 24 * 60 * 60 * 1000;

  for (let index = nightEvents.length - 1; index >= 0; index -= 1) {
    const timing = nightEvents[index];

    if (timing && timing.value.getTime() - oneDay <= timestamp) {
      return timing.event;
    }
  }

  return timings[timings.length - 1]?.event ?? null;
};

export const getNextEvent = (timings: PrayerTiming[], timestamp: number) =>
  timings.find((timing) => timing.value.getTime() > timestamp) ?? null;

export const buildPrayerSnapshot = (
  location: PrayerLocation,
  now: Date,
  config: PrayerMethodConfig,
): PrayerSnapshot => {
  const dayForLocation = getDateForTimeZone(now, location.timeZone);
  const parameters = createCalculationParameters(config);
  const coordinates = new Coordinates(location.lat, location.lon);
  const prayerTimes = new PrayerTimes(coordinates, dayForLocation, parameters);
  const sunnahTimes = new SunnahTimes(prayerTimes);
  const timings = buildTimings(prayerTimes, sunnahTimes, location.timeZone);
  const fardTimings = timings.filter((timing) => timing.isFard);
  const activeEvent = getActiveEvent(fardTimings, now.getTime());
  let nextEvent = getNextEvent(fardTimings, now.getTime());

  if (!nextEvent) {
    const nextDay = getStartOfNextDayInTimeZone(now, location.timeZone);
    const nextDayPrayerTimes = new PrayerTimes(coordinates, nextDay, parameters);
    const nextDaySunnahTimes = new SunnahTimes(nextDayPrayerTimes);
    const nextDayTimings = buildTimings(nextDayPrayerTimes, nextDaySunnahTimes, location.timeZone);
    const nextDayFard = nextDayTimings.filter((timing) => timing.isFard);

    nextEvent = nextDayFard[0] ?? null;
  }

  return {
    activeEvent,
    dateLabel: formatDateLabel(now, location.timeZone),
    localTimeLabel: formatClock(now, location.timeZone),
    locationId: location.id,
    nextEvent: nextEvent?.event ?? null,
    nextEventTime: nextEvent?.value ?? null,
    timings,
    timezoneLabel: shortTimeZone(now, location.timeZone) ?? location.timeZone,
  };
};

export const buildPrayerCatalog = (
  locations: PrayerLocation[],
  now: Date,
  config: PrayerMethodConfig,
): Record<string, PrayerSnapshot> =>
  Object.fromEntries(locations.map((location) => [location.id, buildPrayerSnapshot(location, now, config)]));

export const getNextCatalogRefreshAt = (
  locations: PrayerLocation[],
  snapshots: Record<string, PrayerSnapshot>,
  now: Date,
) => {
  const upcomingTimes = locations
    .map((location) => snapshots[location.id]?.nextEventTime)
    .filter((value): value is Date => value instanceof Date)
    .map((value) => value.getTime())
    .filter((value) => value > now.getTime());

  if (upcomingTimes.length > 0) {
    return Math.min(...upcomingTimes);
  }

  return Math.min(...locations.map((location) => getStartOfNextDayInTimeZone(now, location.timeZone).getTime()));
};
